import React, { useEffect, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  ConfirmationModal,
} from '../../components';
import { GoogleIcon, MicrosoftIcon, PhoneIcon } from './icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useFirebase } from '../../context';
import { useMixPanel } from '../../components/MixPanel/useMixPanel';
import { useGoogleAnalytics } from '../../components/GoogleAnalytics/useGoogleAnalytics';
import { ClientType, UserSignUpFlow } from '../../store/auth/auth.types';
import { getAuthCodes, verifyInviteToken } from '../../store/auth/auth.thunks';
import { ROUTES } from '../../routes';
import {
  ACCOUNT_URL,
  OUTLOOK_ADDIN,
  SUPER_ACCOUNT_URL,
} from '../../constants/config';
import { WarningIcon } from '../../icons';
import {
  clearSignInError,
  closeInvitationErrorModal,
  openInvitationErrorModal,
  resetAuthState,
} from '../../store/auth/auth.slice';
import { withPageTransition } from '../../hoc';
import { MixpanelEvent } from '../../components/MixPanel/events/events';

const Signup: React.FC = () => {
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.auth.user);
  const userSignUpFlow = useAppSelector((state) => state.auth.userSignUpFlow);

  const dashboardAuthCode = useAppSelector(
    (state) => state.auth.dashboardAuthCode
  );

  const superAdminAuthCode = useAppSelector(
    (state) => state.auth.superAdminAuthCode
  );

  const extensionAuthCode = useAppSelector(
    (state) => state.auth.extensionAuthCode
  );

  const isSignInError = useAppSelector((state) => state.auth.isError.signIn);

  const isIdentityError = useAppSelector(
    (state) => state.auth.isError.identity
  );

  const isInvitationErrorModalOpened = useAppSelector(
    (state) => state.auth.isInvitationErrorModalOpened
  );

  const {
    isLoggedIn,
    isInitialized,
    isSignInLoading,
    googleSignIn,
    microsoftSignIn,
    signOut,
  } = useFirebase();

  const history = useHistory();
  const { search } = useLocation();
  const { orgId, token } = useParams<{ orgId: string; token: string }>();

  const { trackEvent, isMixPanelInitialized } = useMixPanel();
  const { trackGAEvent } = useGoogleAnalytics();

  const { returnUrl, client, isOutlookAddInFlow, origin } = useMemo(() => {
    const queryParams = new URLSearchParams(search);

    return {
      returnUrl: decodeURIComponent(queryParams.get('returnUrl') || ''),
      client: queryParams.get('client') as ClientType,
      origin: queryParams.get('origin') || '',
      isOutlookAddInFlow: queryParams.get('flow') === OUTLOOK_ADDIN.FLOW_NAME,
    };
  }, [search]);

  const decriptedTokenInfo = useMemo(() => {
    if (token) {
      const { email, type } = jwtDecode<{
        email: string;
        type?: 'ownerInvite' | 'userInvite';
      }>(token);
      return { email, type };
    }

    return null;
  }, [token]);

  useEffect(() => {
    if (!token) {
      switch (origin) {
        case 'home-page-get-started':
          trackEvent(MixpanelEvent.QUERY_WEBSITE_GET_STARTED);
          break;
        case 'home-page-login':
          trackEvent(MixpanelEvent.QUERY_WEBSITE_LOGIN);
          break;
        case 'pricing-page-get-started':
          trackEvent(MixpanelEvent.QUERY_WEBSITE_PRICING_PAGE_GET_STARTED);
          break;
        default:
          trackEvent(MixpanelEvent.ARRIVED_TO_SIGNUP_PAGE);
      }
    }
  }, [origin, isMixPanelInitialized, token, trackEvent]);

  const emailFromToken = decriptedTokenInfo?.email;
  const inviteType = decriptedTokenInfo?.type;

  useEffect(() => {
    if (isMixPanelInitialized && inviteType) {
      if (inviteType === 'ownerInvite') {
        trackEvent(MixpanelEvent.QUERY_MAIL_OWNER_INVITE);
      } else if (inviteType === 'userInvite') {
        trackEvent(MixpanelEvent.QUERY_MAIL_USER_INVITE);
      }
    }
  }, [isMixPanelInitialized, inviteType, trackEvent]);

  useEffect(() => {
    if (isOutlookAddInFlow) {
      window.name = OUTLOOK_ADDIN.FLOW_NAME;
    }
  }, [isOutlookAddInFlow]);

  // Get auth codes for apps
  useEffect(() => {
    if (emailFromToken && user && user.email !== emailFromToken) {
      return;
    }

    if (
      isLoggedIn &&
      userSignUpFlow &&
      userSignUpFlow === UserSignUpFlow.COMPLETED
    ) {
      dispatch(getAuthCodes({ client }));
    }
  }, [client, dispatch, emailFromToken, isLoggedIn, user, userSignUpFlow]);

  // Verify invite token
  useEffect(() => {
    if (orgId && token) {
      dispatch(verifyInviteToken({ orgId, token }));
    }
  }, [orgId, token, dispatch]);

  // Handle redirects
  useEffect(() => {
    if (!user) {
      return;
    }

    if (emailFromToken && user.email !== emailFromToken) {
      signOut();
      dispatch(resetAuthState());
      dispatch(openInvitationErrorModal());
      return;
    }

    if (userSignUpFlow && userSignUpFlow !== UserSignUpFlow.COMPLETED) {
      history.push(ROUTES.PRICING);
      return;
    }

    if (
      superAdminAuthCode &&
      window.name !== OUTLOOK_ADDIN.FLOW_NAME &&
      (!returnUrl || returnUrl === SUPER_ACCOUNT_URL)
    ) {
      window.location.href = `${SUPER_ACCOUNT_URL}?authCode=${superAdminAuthCode}`;
      return;
    }

    if (
      extensionAuthCode &&
      window.name !== OUTLOOK_ADDIN.FLOW_NAME &&
      returnUrl?.includes('mail.google.com')
    ) {
      window.location.href = `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(
        user.email
      )}`;
      return;
    }

    if (dashboardAuthCode && window.name !== OUTLOOK_ADDIN.FLOW_NAME) {
      window.location.href = `${ACCOUNT_URL}?authCode=${dashboardAuthCode}`;
    }
  }, [
    dashboardAuthCode,
    dispatch,
    emailFromToken,
    extensionAuthCode,
    history,
    returnUrl,
    signOut,
    superAdminAuthCode,
    user,
    userSignUpFlow,
  ]);

  const onGoogleSignIn = () => {
    trackEvent(MixpanelEvent.CONTINUE_WITH_GOOGLE_CLICK);

    if (emailFromToken) {
      googleSignIn(emailFromToken);
    } else {
      googleSignIn();
    }
  };

  const onSignInWithMicrosoft = () => {
    // if (emailFromToken) {
    //   microsoftSignIn(emailFromToken);
    // } else {
    //   microsoftSignIn();
    // }
    trackEvent(MixpanelEvent.CONTINUE_WITH_MICROSOFT_CLICK);

    microsoftSignIn();
  };

  const onTalkToUs = () => {
    trackEvent(MixpanelEvent.SIGNUP_TALK_TO_SALES_CLICK);
    window.open('https://www.substrata.me/get-a-demo/', '_blank');
  };

  const renderErrorModalDescription = () => {
    const props = {
      supportLink: {
        className: 'signup__email-link',
        href: 'mailto:support@substrata.me',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    };

    return (
      <>
        This email address is either not associated with a user or it has not
        beed activated. To get started, please reach out to{' '}
        <a {...props.supportLink}>support@substrata.me</a>
      </>
    );
  };

  const renderInvitationErrorModalDescription = () => {
    const props = {
      supportLink: {
        className: 'signup__email-link',
        href: 'mailto:support@substrata.me',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    };

    return (
      <>
        This email address is not associated with an active invitation. Please
        use the email address you were invited with or contact support at{' '}
        <a {...props.supportLink}>support@substrata.me</a> for assistance.
      </>
    );
  };

  const attrs = {
    container: {
      className: 'signup',
    },
    left: {
      wrapper: {
        className: 'signup__left',
      },
      content: {
        wrapper: {
          className: 'signup__left__content',
        },
        title: {
          className: 'signup__left__content-title',
        },
        subtitle: {
          className: 'signup__left__content-subtitle',
        },
        buttons: {
          className: 'signup__left__content__buttons',
        },
      },
      footer: {
        wrapper: {
          className: 'signup__left__footer',
        },
      },
    },
    right: {
      className: 'signup__right',
    },
    title: {
      className: 'signup__title',
    },
    bottom: {
      className: 'signup__bottom',
    },
    buttons: {
      className: 'signup__buttons',
    },
    googleButton: {
      size: ButtonSize.S,
      variant: ButtonVariant.Secondary,
      className: 'signup__google-button',
      disabled: !isInitialized || isLoggedIn || isSignInLoading,
      onClick: onGoogleSignIn,
    },
    microsoftButton: {
      size: ButtonSize.S,
      variant: ButtonVariant.Secondary,
      className: 'signup__google-button',
      disabled: !isInitialized || isLoggedIn || isSignInLoading,
      onClick: onSignInWithMicrosoft,
    },
    TalkToUsButton: {
      size: ButtonSize.S,
      variant: ButtonVariant.Secondary,
      className: 'signup__google-button',
      disabled: !isInitialized || isLoggedIn || isSignInLoading,
      onClick: onTalkToUs,
    },
    footer: {
      className: 'signup__footer',
    },
    contactUsLink: {
      type: 'button' as const,
      className: 'signup__link',
      onClick: () => {
        window.open('https://www.substrata.me/company/contact/', '_blank');
        trackEvent(MixpanelEvent.SIGNUP_CONTACT_US_CLICK);
        trackGAEvent('event', 'signup.homepage.open-contact-center-clicked');
      },
    },
    termsOfServiceLink: {
      type: 'button' as const,
      className: 'signup__link',
      onClick: () => {
        window.open('https://www.substrata.me/terms-of-service/', '_blank');
        trackEvent(MixpanelEvent.SIGNUP_TERMS_OF_USE_CLICK);
        trackGAEvent('event', 'signup.homepage.open-terms-of-use-clicked');
      },
    },
    privacyPolicyLink: {
      type: 'button' as const,
      className: 'signup__link',
      onClick: () => {
        window.open('https://www.substrata.me/privacy-policy/', '_blank');
        trackEvent(MixpanelEvent.SIGNUP_PRIVACY_POLICY_CLICK);
        trackGAEvent('event', 'signup.homepage.open-privacy-policy-clicked');
      },
    },
    errorModal: {
      icon: WarningIcon,
      isOpened: isSignInError || isIdentityError,
      title: 'Could not log you in',
      description: renderErrorModalDescription(),
      onClose: () => {
        dispatch(clearSignInError());
      },
    },
    invitationErrorModal: {
      icon: WarningIcon,
      isOpened: isInvitationErrorModalOpened,
      title: 'Could not log you in',
      description: renderInvitationErrorModalDescription(),
      onClose: () => {
        dispatch(closeInvitationErrorModal());
      },
    },
  };

  return (
    <>
      <div {...attrs.container}>
        <div {...attrs.left.wrapper}>
          <div {...attrs.left.content.wrapper}>
            <div {...attrs.left.content.title}>Welcome to Substrata</div>
            <div {...attrs.left.content.subtitle}>
              To create your account, choose your preferred sign-up method
              below:
            </div>
            <div {...attrs.left.content.buttons}>
              {!(
                isOutlookAddInFlow || window.name === OUTLOOK_ADDIN.FLOW_NAME
              ) ? (
                <Button {...attrs.googleButton}>
                  <GoogleIcon />
                  <span>Continue with Google</span>
                </Button>
              ) : null}
              <Button {...attrs.microsoftButton}>
                <MicrosoftIcon />
                <span>Continue with Microsoft</span>
              </Button>
              <Button {...attrs.TalkToUsButton}>
                <PhoneIcon />
                <span>Talk to Us</span>
              </Button>
            </div>
          </div>
          <div {...attrs.left.footer.wrapper}>
            <span>
              Can’t sign up?{' '}
              <button {...attrs.contactUsLink}>Contact Us</button>
            </span>
            <span>
              By signing up, you agree to our{' '}
              <button {...attrs.termsOfServiceLink}>Terms of Service</button>{' '}
              and <button {...attrs.privacyPolicyLink}>Privacy Policy</button>
            </span>
          </div>
        </div>
        <div {...attrs.right} />
      </div>
      <ConfirmationModal {...attrs.errorModal} />
      <ConfirmationModal {...attrs.invitationErrorModal} />
    </>
  );
};

export default withPageTransition(Signup);
